/* eslint-disable @typescript-eslint/no-explicit-any */
import { toast } from "sonner"

import type {
  DefaultOptions,
  QueryClient,
  QueryKey,
  UseMutationOptions,
} from "@tanstack/react-query"

export const queryConfig = {
  queries: {
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 1000 * 60,
  },
  mutations: {
    retry: false,
  },
} satisfies DefaultOptions

export type ApiFnReturnType<FnType extends (...args: any) => Promise<any>> = Awaited<
  ReturnType<FnType>
>

export type QueryConfig<T extends (...args: any[]) => any> = Omit<
  ReturnType<T>,
  "queryKey" | "queryFn"
>

export type MutationConfig<MutationFnType extends (...args: any) => Promise<any>> =
  UseMutationOptions<ApiFnReturnType<MutationFnType>, Error, Parameters<MutationFnType>[0]>

/**
 * Standard mutation options helper for common patterns
 */
export type StandardMutationOptions<TData, TVariables, TContext = unknown> = {
  /**
   * Query keys to invalidate on success
   */
  invalidateQueries?: QueryKey[]
  /**
   * Success message to show (optional, defaults to generic success)
   */
  successMessage?: string
  /**
   * Error message to show (optional, defaults to generic error)
   */
  errorMessage?: string
  /**
   * Callback on successful mutation
   */
  onSuccess?: (data: TData, variables: TVariables) => void | Promise<void>
  /**
   * Callback on error
   */
  onError?: (error: Error, variables: TVariables) => void
  /**
   * Optimistic update function - returns the optimistic data and context for rollback
   */
  optimisticUpdate?: (variables: TVariables) => {
    queryKey: QueryKey
    data: unknown
    context?: TContext
  }
  /**
   * Rollback function for optimistic updates
   */
  onRollback?: (context: TContext) => void
}

/**
 * Creates standard mutation options with cache invalidation and error handling
 */
export function createStandardMutationOptions<TData, TVariables, TContext = unknown>(
  queryClient: QueryClient,
  options: StandardMutationOptions<TData, TVariables, TContext> = {}
): Partial<UseMutationOptions<TData, Error, TVariables, TContext>> {
  const {
    invalidateQueries = [],
    successMessage,
    errorMessage,
    onSuccess,
    onError,
    optimisticUpdate,
    onRollback,
  } = options

  return {
    onMutate: optimisticUpdate
      ? async (variables: TVariables) => {
          const update = optimisticUpdate(variables)
          // Cancel outgoing queries
          await queryClient.cancelQueries({ queryKey: update.queryKey })

          // Snapshot previous value
          const previous = queryClient.getQueryData(update.queryKey)

          // Optimistically update
          queryClient.setQueryData(update.queryKey, update.data)

          return { previous, ...update.context } as TContext
        }
      : undefined,
    onSuccess: async (data: TData, variables: TVariables) => {
      // Invalidate queries
      for (const queryKey of invalidateQueries) {
        await queryClient.invalidateQueries({ queryKey })
      }

      // Show success message
      if (successMessage) {
        toast.success(successMessage)
      }

      // Call custom success handler
      await onSuccess?.(data, variables)
    },
    onError: (error: Error, variables: TVariables, context: TContext | undefined) => {
      // Rollback optimistic update if applicable
      if (context && optimisticUpdate && onRollback) {
        onRollback(context)
      } else if (context && optimisticUpdate) {
        // Default rollback: restore previous data
        const update = optimisticUpdate(variables)
        if ((context as any).previous !== undefined) {
          queryClient.setQueryData(update.queryKey, (context as any).previous)
        }
      }

      // Show error message
      toast.error(errorMessage || error.message || "An error occurred")

      // Call custom error handler
      onError?.(error, variables)
    },
  }
}
